import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Save, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["incomplete_days", "Abandoned / incomplete applications", "Deleted this many days after last activity."],
  ["withdrawn_days", "Withdrawn applications", "Deleted this many days after withdrawal."],
  ["not_selected_days", "Not-selected applications", "Kept longer for fair-housing recordkeeping."],
  ["audit_log_days", "Audit log entries", "Purge-evidence records are always preserved."],
];

export default function AdminRetention() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { api.get("/admin/retention").then((res) => setR(res.data)); }, []);
  if (!r) return <div className="p-8" style={{ color: "#475569" }}>Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/retention", {
        incomplete_days: Number(r.incomplete_days),
        withdrawn_days: Number(r.withdrawn_days),
        not_selected_days: Number(r.not_selected_days),
        audit_log_days: Number(r.audit_log_days),
        auto_purge_enabled: !!r.auto_purge_enabled,
      });
      setR(data);
      toast.success("Retention settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const runPurge = async (dryRun) => {
    if (!dryRun && !window.confirm("Run the purge now? Records past their retention window will be permanently deleted (a minimal anonymized audit record is kept for each).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/retention/purge?dry_run=${dryRun}`);
      setPreview(data);
      toast.success(dryRun ? "Preview complete — nothing was deleted." : "Purge complete.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl" style={{ color: "#0B2D59" }}>Data retention</h1>
      <p className="text-sm mt-1" style={{ color: "#475569" }}>
        Old records are removed on a schedule so applicant information is never kept longer than needed.
        Selected / current-tenant records are <strong>never</strong> auto-purged. Each purged record leaves a
        minimal, anonymized audit entry (confirmation number, status, dates — no personal details).
      </p>

      <div className="p2p-card p-6 mt-5 space-y-4">
        {FIELDS.map(([key, label, help]) => (
          <div key={key} className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium" style={{ color: "#0B2D59" }}>{label}</div>
              <div className="text-xs" style={{ color: "#475569" }}>{help}</div>
            </div>
            <div className="flex items-center gap-2">
              <input type="number" className="p2p-input" style={{ width: "110px" }}
                data-testid={`retention-${key}`}
                value={r[key]} onChange={(e) => setR({ ...r, [key]: e.target.value })} />
              <span className="text-sm" style={{ color: "#475569" }}>days</span>
            </div>
          </div>
        ))}
        <label className="flex items-center gap-3 pt-2 border-t" style={{ borderColor: "#E2EBF1" }}>
          <input type="checkbox" checked={!!r.auto_purge_enabled}
            data-testid="retention-auto-purge"
            onChange={(e) => setR({ ...r, auto_purge_enabled: e.target.checked })} />
          <span className="text-sm" style={{ color: "#0B2D59" }}>Run the purge automatically once a day</span>
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button className="p2p-btn-primary inline-flex items-center gap-2" onClick={save} disabled={busy}
          data-testid="retention-save">
          <Save className="w-4 h-4" /> Save settings
        </button>
        <button className="p2p-btn-outline inline-flex items-center gap-2" onClick={() => runPurge(true)} disabled={busy}
          data-testid="retention-preview">
          <Eye className="w-4 h-4" /> Preview purge (dry run)
        </button>
        <button className="p2p-btn-secondary inline-flex items-center gap-2" onClick={() => runPurge(false)} disabled={busy}
          data-testid="retention-run-purge">
          <Trash2 className="w-4 h-4" /> Run purge now
        </button>
      </div>

      {preview && (
        <div className="p2p-card p-5 mt-5 text-sm" data-testid="retention-results" style={{ color: "#0B2D59" }}>
          <div className="font-serif text-lg mb-2">{preview.dry_run ? "Preview — would be removed" : "Removed"}</div>
          <ul className="space-y-1">
            <li>Abandoned / incomplete applications: <strong>{preview.incomplete ?? 0}</strong></li>
            <li>Withdrawn applications: <strong>{preview.withdrawn ?? 0}</strong></li>
            <li>Not-selected applications: <strong>{preview.not_selected ?? 0}</strong></li>
            <li>Aged-out audit log entries: <strong>{preview.audit_logs ?? 0}</strong></li>
          </ul>
        </div>
      )}

      <p className="p2p-help mt-5 text-xs">
        Uploaded documents live in secured object storage and are referenced only through the application
        record. When a record is purged, its documents become permanently unreachable.
      </p>
    </div>
  );
}
